import {
  type Announcements,
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  KeyboardSensor,
  PointerSensor,
  type ScreenReaderInstructions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IContextualMenuItem, IContextualMenuProps, IStackStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPSpinner, IPText } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import PowerBIReport from 'components/PowerBI/PowerBIReport';
import {
  configIcon,
  deleteIcon,
  globalBoxBorder,
  globalBoxBorderDarkMode,
  globalBoxBorderRadius,
  globalBoxShadow,
  globalPaddingScene,
  globalStackTokensGapMedium,
  globalTextStylesBold,
  leftArrowIcon,
  newIcon,
  rightArrowIcon,
} from 'globalStyles';
import Dashboard, { DashboardSharedData, DashboardTypes } from 'models/dashboard';
import Widget, { getWidgetTypes, IWidgetCustomAction, WidgetSizes, WidgetWidth } from 'models/widget';
import { type CSSProperties, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiAddWidgetToDashboard,
  apiGetDashboardWidgets,
  apiRemoveWidgetFromDashboard,
  apiUpdateWidgetOnDashboard,
} from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import AddWidgetPanel from './AddWidgetPanel';
import EditWidgetPanel from './EditWidgetPanel';
import { WidgetDrillDownContext } from './WidgetDrillDownContext';
import WidgetRenderer from './WidgetRenderer';

interface IWidgetDragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

interface ISortableDashboardWidgetProps {
  id: number;
  disabled: boolean;
  children: (dragHandleProps: IWidgetDragHandleProps) => ReactNode;
}

const SortableDashboardWidget = (props: ISortableDashboardWidgetProps) => {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.id,
    disabled: props.disabled,
  });
  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : undefined,
    position: 'relative',
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {props.children({ attributes, listeners, isDragging })}
    </div>
  );
};

interface IDashboardInstanceProps {
  dashboard: Dashboard;
  readonly: boolean;
  sharedData?: DashboardSharedData;
  period?: IPeriodFilterData;
  onRefreshSharedData?: () => Promise<void>;

  /** Polar project overview: scopes each widget's own query to this project. */
  projectId?: number;
}

const DashboardInstance = (props: IDashboardInstanceProps) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState<boolean>(false);
  const [isMutatingWidget, setIsMutatingWidget] = useState<boolean>(false);
  const [addWidget, setAddWidget] = useState<Widget | undefined>(undefined);
  const [deleteWidget, setDeleteWidget] = useState<Widget | undefined>(undefined);
  const [configureWidget, setConfigureWidget] = useState<Widget | undefined>(undefined);
  const [currentId, setCurrentId] = useState<number | undefined>(undefined);
  const widgetAbortControllerRef = useRef<AbortController>(new AbortController());
  const isReorderingWidgetRef = useRef<boolean>(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const maxWidgetsPerDashboards = 12;

  //
  // Effects
  //
  useEffect(() => {
    const controller = widgetAbortControllerRef.current;

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      try {
        if (isLoadingWidgets) return;
        if (props.dashboard.dashboardId === currentId) return;
        if (props.dashboard.url) return;

        setCurrentId(props.dashboard.dashboardId);
        setIsLoadingWidgets(true);

        await appContext.globalDataCache.norms.getItems();

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        if (controller.signal.aborted) return;

        let widgets = await apiGetDashboardWidgets(
          props.dashboard.dashboardId,
          accessToken,
          appContext.globalDataCache,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        widgets
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((w) => {
            w.dashboard = props.dashboard;
            // The backend only returns the translation in the current language. Manually set the object here.
            w.trans.forEach((t) => (t.lang = appContext.user.language));

            return w;
          });

        widgets = [...props.dashboard.widgets, ...widgets]; //add widgets to a dashboard which can already contain system widgets

        setWidgets(widgets);
      } catch (err) {
        if (controller.signal.aborted) return;
        appContext.setError(err);
      } finally {
        setIsLoadingWidgets(false);
        if (controller.signal.aborted) return;
      }
    };

    loadData();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dashboard]);

  //
  // Update widgets when period changes to trigger re-render of widgets with new period
  //
  useEffect(() => {
    setWidgets([...widgets.map((w) => w.clone())]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.period]);

  //
  // Styles
  //
  const getWidgetStyle = (size: WidgetSizes) => {
    const width = WidgetWidth[size];

    return {
      root: {
        border: appContext.useDarkMode ? globalBoxBorderDarkMode : globalBoxBorder,
        borderRadius: globalBoxBorderRadius,
        boxShadow: globalBoxShadow,
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
        paddingRight: 10,
        height: 300,
        width: width,
      },
    };
  };

  const getAddWidgetStyle = () => {
    return {
      root: {
        boxShadow: globalBoxShadow,
        borderRadius: globalBoxBorderRadius,
        color: 'lightgrey',
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
        paddingRight: 10,
        height: 300,
        width: 300,
        selectors: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      },
    };
  };

  const stackStylesWidget: IStackStyles = {
    root: {
      paddingLeft: globalPaddingScene,
      paddingRight: globalPaddingScene,
      paddingTop: globalPaddingScene,
      paddingBottom: globalPaddingScene,
    },
  };

  //
  // Helpers
  //
  const getWidgetFromId = (id: number): Widget => {
    return widgets.find((d) => d.widgetId === id) || new Widget();
  };

  const getMovableWidgets = (): Widget[] => {
    return widgets.filter((widget) => !isSystem(widget.widgetId));
  };

  const getMovableWidgetIndexFromId = (id: number): number => {
    return getMovableWidgets().findIndex((widget) => widget.widgetId === id);
  };

  const canAddWidget = (id: number): boolean => {
    if (widgets.length < maxWidgetsPerDashboards) {
      return !isSystem(id) || id === -1; //add is enabled on the last system widget
    }

    return false;
  };

  const isSystem = (id: number): boolean => {
    return id < 0;
  };

  //
  // Reorder widgets
  //
  const moveWidget = async (id: number, requestedIndex: number) => {
    if (isMutatingWidget || isReorderingWidgetRef.current || isSystem(id)) return;

    const previousWidgets = widgets;
    const movableWidgets = getMovableWidgets();
    const sourceIndex = movableWidgets.findIndex((widget) => widget.widgetId === id);
    const targetIndex = Math.max(0, Math.min(requestedIndex, movableWidgets.length - 1));
    if (sourceIndex < 0 || sourceIndex === targetIndex) return;

    const reorderedMovableWidgets = arrayMove(movableWidgets, sourceIndex, targetIndex).map((widget, index) => {
      const reorderedWidget = widget.clone();
      reorderedWidget.customActions = widget.customActions;
      reorderedWidget.sortOrder = index;

      return reorderedWidget;
    });
    let movableIndex = 0;
    const reorderedWidgets = widgets.map((widget) =>
      isSystem(widget.widgetId) ? widget : reorderedMovableWidgets[movableIndex++],
    );
    const movedWidget = reorderedMovableWidgets[targetIndex];

    isReorderingWidgetRef.current = true;
    setIsMutatingWidget(true);
    setWidgets(reorderedWidgets);

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateWidgetOnDashboard(movedWidget, accessToken, appContext.globalDataCache);
    } catch (err) {
      setWidgets(previousWidgets);
      appContext.setError(err);
    } finally {
      isReorderingWidgetRef.current = false;
      setIsMutatingWidget(false);
    }
  };

  const moveLeft = (id: number) => {
    void moveWidget(id, getMovableWidgetIndexFromId(id) - 1);
  };

  const moveRight = (id: number) => {
    void moveWidget(id, getMovableWidgetIndexFromId(id) + 1);
  };

  const canMoveLeft = (id: number): boolean => {
    return getMovableWidgetIndexFromId(id) > 0 && !isMutatingWidget;
  };

  const canMoveRight = (id: number): boolean => {
    const idx = getMovableWidgetIndexFromId(id);

    return idx >= 0 && idx < getMovableWidgets().length - 1 && !isMutatingWidget;
  };

  const sortableWidgetIds = props.readonly ? [] : getMovableWidgets().map((widget) => widget.widgetId);
  const showWidgetDragHandles = sortableWidgetIds.length > 1;

  const getWidgetReorderData = (id: number, positionId: number = id) => {
    const widget = getWidgetFromId(id);

    return {
      count: widgets.length,
      name: widget.getName(appContext, t),
      position: widgets.findIndex((positionWidget) => positionWidget.widgetId === positionId) + 1,
    };
  };

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: t('dashboard:WidgetReorder.DragInstructions'),
  };
  const announcements: Announcements = {
    onDragStart: ({ active }) => t('dashboard:WidgetReorder.DragStarted', getWidgetReorderData(Number(active.id))),
    onDragOver: ({ active, over }) => {
      if (!over) return undefined;

      return t('dashboard:WidgetReorder.DragOver', getWidgetReorderData(Number(active.id), Number(over.id)));
    },
    onDragEnd: ({ active, over }) => {
      if (!over) return undefined;

      return t('dashboard:WidgetReorder.DragDropped', getWidgetReorderData(Number(active.id), Number(over.id)));
    },
    onDragCancel: ({ active }) => t('dashboard:WidgetReorder.DragCancelled', getWidgetReorderData(Number(active.id))),
  };

  const handleWidgetDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;

    const targetIndex = getMovableWidgetIndexFromId(Number(event.over.id));
    if (targetIndex < 0) return;

    void moveWidget(Number(event.active.id), targetIndex);
  };

  //
  // Menu actions
  //
  const getMenuItems = (widget: Widget): IContextualMenuProps => {
    let menuProps: IContextualMenuProps = { items: [] };
    const widgetId = widget.widgetId;

    if (!isSystem(widget.widgetId) && !props.readonly) {
      menuProps = {
        items: [
          {
            key: 'add',
            data: widgetId,
            disabled: !canAddWidget(widgetId),
            iconProps: newIcon,
            text: t('dashboard:WidgetMenu.New'),
            onClick: () => setAddWidget(getWidgetFromId(widgetId)),
          },
          { key: 'd1', itemType: IPContextualMenuItemType.Divider },
          {
            key: 'moveleft',
            data: widgetId,
            disabled: !canMoveLeft(widgetId),
            iconProps: leftArrowIcon,
            text: t('dashboard:WidgetMenu.MoveLeft'),
            onClick: () => moveLeft(widgetId),
          },
          {
            key: 'moveright',
            data: widgetId,
            disabled: !canMoveRight(widgetId),
            iconProps: rightArrowIcon,
            text: t('dashboard:WidgetMenu.MoveRight'),
            onClick: () => moveRight(widgetId),
          },
          {
            key: 'moveto',
            data: widgetId,
            disabled: getMovableWidgets().length <= 1 || isMutatingWidget,
            iconProps: { iconName: 'NumberedList' },
            text: t('dashboard:WidgetMenu.MoveTo'),
            subMenuProps: {
              items: getMovableWidgets().map((positionWidget, index) => ({
                key: `position-${index}`,
                canCheck: true,
                checked: getMovableWidgetIndexFromId(widgetId) === index,
                disabled: getMovableWidgetIndexFromId(widgetId) === index || isMutatingWidget,
                text: t('dashboard:WidgetMenu.Position', {
                  position: widgets.findIndex((widget) => widget.widgetId === positionWidget.widgetId) + 1,
                }),
                onClick: () => {
                  void moveWidget(widgetId, index);
                },
              })),
            },
          },
          { key: 'd3', itemType: IPContextualMenuItemType.Divider },
          {
            key: 'delete',
            data: widgetId,
            disabled: isSystem(widgetId),
            iconProps: deleteIcon,
            text: t('dashboard:WidgetMenu.Delete'),
            onClick: () => setDeleteWidget(getWidgetFromId(widgetId)),
          },
          { key: 'd2', itemType: IPContextualMenuItemType.Divider },
          {
            key: 'config',
            data: widgetId,
            disabled: isSystem(widgetId),
            iconProps: configIcon,
            text: t('dashboard:WidgetMenu.Configure'),
            onClick: () => setConfigureWidget(getWidgetFromId(widgetId)),
          },
        ],
      };
    }

    if (widget.customActions && widget.customActions.length > 0) {
      menuProps.items.push(
        ...widget.customActions.map((action, index) => {
          const customAction: IContextualMenuItem = {
            key: `custom-${widget.widgetId}-${index}`,
            data: widgetId,
            iconProps: action.iconProps,
            disabled: action.disabled,
            text: action.name,
            onClick: () => action.callback(widget),
          };

          return customAction;
        }),
      );
    }

    return menuProps;
  };

  const onEditWidget = async (widget: Widget) => {
    try {
      if (isMutatingWidget) return;
      setIsMutatingWidget(true);

      if (
        widget.name.trim() === '' ||
        getWidgetTypes(appContext, t).find((w) => w.type === widget.widgetType)?.name === widget.name
      ) {
        // If the same or an empty name was entered, leave empty.
        widget.name = '';
        widget.trans = [];
        widget.transIdx = -1;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      widget = await apiUpdateWidgetOnDashboard(widget, accessToken, appContext.globalDataCache);

      widget.dashboard = props.dashboard;
      widget.trans.forEach((t) => (t.lang = appContext.user.language));

      const newWidgets = widgets.map((w) => (w.widgetId === widget.widgetId ? widget : w));
      setWidgets(newWidgets);
      setConfigureWidget(undefined);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsMutatingWidget(false);
    }
  };

  const onAddWidget = async (widget: Widget) => {
    try {
      if (isMutatingWidget || !addWidget) return;
      setIsMutatingWidget(true);

      widget.dashboardId = props.dashboard.dashboardId;
      widget.sortOrder = addWidget?.sortOrder + 1;

      if (
        widget.name.trim() === '' ||
        getWidgetTypes(appContext, t).find((w) => w.type === widget.widgetType)?.name === widget.name
      ) {
        // If the same name was entered, leave empty.
        widget.name = '';
        widget.trans = [];
        widget.transIdx = -1;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newWidget = await apiAddWidgetToDashboard(widget, accessToken, appContext.globalDataCache);
      newWidget.dashboard = props.dashboard;
      newWidget.trans.forEach((t) => (t.lang = appContext.user.language));

      const idx = widgets.findIndex((w) => w.widgetId === addWidget.widgetId);
      const before = widgets.slice(0, idx + 1);
      const after = widgets.slice(idx + 1);
      const newWidgets = [...before, newWidget, ...after];

      setWidgets(newWidgets);
      setAddWidget(undefined);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsMutatingWidget(false);
    }
  };

  const onDeleteWidget = async () => {
    try {
      if (isMutatingWidget || !deleteWidget) return;
      setIsMutatingWidget(true);
      setDeleteWidget(undefined);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiRemoveWidgetFromDashboard(deleteWidget, accessToken, appContext.globalDataCache);
      const newWidgets = widgets.filter((d) => d.widgetId !== deleteWidget.widgetId);

      setWidgets(newWidgets);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsMutatingWidget(false);
    }
  };

  const registerCustomActions = (widget: Widget, customActions: IWidgetCustomAction[]) => {
    widget.customActions = customActions;
    setWidgets([...widgets]);
  };

  const renderWidget = (widget: Widget, dragHandleProps?: IWidgetDragHandleProps, key?: number) => {
    return (
      <IPStack.Item key={key} styles={getWidgetStyle(widget.widgetSize)}>
        <IPStack verticalFill>
          <IPStack.Item>
            <IPStack horizontal horizontalAlign="space-between">
              <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
                <IPText block={true} nowrap={true} styles={globalTextStylesBold}>
                  {widget.getName(appContext, t)}
                </IPText>
              </IPStack.Item>
              {dragHandleProps && showWidgetDragHandles && (
                <IPStack.Item>
                  <IPIconButton
                    {...dragHandleProps.attributes}
                    {...dragHandleProps.listeners}
                    disabled={isMutatingWidget}
                    iconProps={{ iconName: 'GripperDotsVertical' }}
                    styles={{
                      root: { cursor: dragHandleProps.isDragging ? 'grabbing' : 'grab', touchAction: 'none' },
                      rootDisabled: { cursor: 'default' },
                    }}
                    title={t('dashboard:WidgetReorder.DragHandle')}
                  />
                </IPStack.Item>
              )}
              {((!isSystem(widget.widgetId) && !props.readonly) ||
                (widget.customActions && widget.customActions.length > 0)) && (
                <IPStack.Item>
                  <IPIconButton menuProps={getMenuItems(widget)} />
                </IPStack.Item>
              )}
            </IPStack>
          </IPStack.Item>
          <IPStack.Item grow>
            <WidgetRenderer
              widget={widget}
              onRegisterCustomActions={registerCustomActions}
              sharedData={props.sharedData}
              onRefreshSharedData={props.onRefreshSharedData}
              period={props.period}
              projectId={props.projectId}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  if (isLoadingWidgets) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (props.dashboard.url) {
    return (
      <IPStack styles={{ root: { height: appContext.windowHeight - 200, paddingRight: 20 } }}>
        <IPStack.Item grow>
          <PowerBIReport url={props.dashboard.url} />
        </IPStack.Item>
      </IPStack>
    );
  }

  if (!isLoadingWidgets && widgets.length === 0 && props.readonly) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItems')}</IPText>
      </IPStack>
    );
  }

  const drillDownDisabled =
    props.dashboard.dashboardType === DashboardTypes.tenant || props.dashboard.dashboardType === DashboardTypes.project;

  return (
    <WidgetDrillDownContext.Provider value={drillDownDisabled}>
      <DndContext
        accessibility={{ announcements, screenReaderInstructions }}
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={handleWidgetDragEnd}
      >
        <SortableContext items={sortableWidgetIds} strategy={rectSortingStrategy}>
          <IPStack horizontal wrap styles={stackStylesWidget} tokens={globalStackTokensGapMedium}>
            {widgets.map((widget) => {
              if (isSystem(widget.widgetId) || props.readonly) {
                return renderWidget(widget, undefined, widget.widgetId);
              }

              return (
                <SortableDashboardWidget
                  key={widget.widgetId}
                  id={widget.widgetId}
                  disabled={isMutatingWidget || !showWidgetDragHandles}
                >
                  {(dragHandleProps) => renderWidget(widget, dragHandleProps)}
                </SortableDashboardWidget>
              );
            })}
            {widgets.length < maxWidgetsPerDashboards && !props.readonly && (
              <IPStack.Item styles={getAddWidgetStyle()}>
                <IPStack
                  className="isoplanner-dashboard-add-widget"
                  verticalFill
                  horizontalAlign="center"
                  verticalAlign="center"
                  onClick={() => setAddWidget(widgets.length > 0 ? widgets[widgets.length - 1] : new Widget())}
                >
                  <IPStack.Item>
                    <IPIconButton
                      iconProps={newIcon}
                      onClick={() => setAddWidget(widgets.length > 0 ? widgets[widgets.length - 1] : new Widget())}
                    />
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            )}
            {addWidget !== undefined && !isMutatingWidget && (
              <AddWidgetPanel
                dashboard={props.dashboard}
                isOpen={addWidget !== undefined}
                onClose={() => setAddWidget(undefined)}
                onAdd={onAddWidget}
                allowSharedDataWidgets={props.sharedData !== undefined}
                period={props.period}
              ></AddWidgetPanel>
            )}
            {configureWidget !== undefined && !isMutatingWidget && (
              <EditWidgetPanel
                widget={configureWidget}
                isOpen={configureWidget !== undefined}
                onClose={() => setConfigureWidget(undefined)}
                onEdit={onEditWidget}
                period={props.period}
              ></EditWidgetPanel>
            )}
            <DialogYesNo
              onNo={() => {
                setDeleteWidget(undefined);
              }}
              onYes={() => {
                onDeleteWidget();
              }}
              title={t('dashboard:Dialogs.DeleteWidget.Title')}
              subText={t('dashboard:Dialogs.DeleteWidget.SubTitle')}
              hidden={deleteWidget === undefined}
            />
          </IPStack>
        </SortableContext>
      </DndContext>
    </WidgetDrillDownContext.Provider>
  );
};

export default DashboardInstance;
